import { HubItemStatisticsStatus } from "client/Api/Analytics/analyticsStatistics";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useHubDetailsDialog } from "client/Features/Hub/HubDetailsDialog";
import { useEditAnnouncementDialog } from "client/Features/Hub/Management/AnnouncementManagement/EditAnnouncementDialog/EditAnnouncementDialog";
import { useEditEventDialog } from "client/Features/Hub/Management/EventsManagement/EditEventDialog/EditEventDialog";
import { useEditKnowledgeItemDialog } from "client/Features/Hub/Management/KnowledgeItemsManagement/EditKnowledgeItemDialog/EditKnowledgeItemDialog";
import { isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { ReactElement, useCallback } from "react";

export function useOpenHubItem(): [
	element: ReactElement | null,
	open: (
		status: Exclude<HubItemStatisticsStatus, HubItemStatisticsStatus.Deleted | HubItemStatisticsStatus.NoAccess>,
		kind: HubItemKind,
		id: number
	) => Promise<void>
] {
	const [detailsDialog, openDetailsDialog] = useHubDetailsDialog();
	const [editAnnouncementDialog, openEditAnnouncementDialog] = useEditAnnouncementDialog();
	const [editKnowledgeItemDialog, openEditKnowledgeItemDialog] = useEditKnowledgeItemDialog();
	const [editEventDialog, openEditEventDialog] = useEditEventDialog();

	const open = useCallback(
		async (
			status: Exclude<HubItemStatisticsStatus, HubItemStatisticsStatus.Deleted | HubItemStatisticsStatus.NoAccess>,
			kind: HubItemKind,
			id: number
		): Promise<void> => {
			switch (status) {
				case HubItemStatisticsStatus.Published:
					await openDetailsDialog({
						id,
						itemKind: kind
					});
					break;
				case HubItemStatisticsStatus.NotPublished:
					switch (kind) {
						case HubItemKind.Announcement:
							await openEditAnnouncementDialog({
								mode: {
									kind: "edit",
									announcementId: id
								}
							});
							break;
						case HubItemKind.KnowledgeItem:
							await openEditKnowledgeItemDialog({
								mode: {
									kind: "edit",
									itemId: id
								}
							});
							break;
						case HubItemKind.Event:
							await openEditEventDialog({
								mode: {
									kind: "edit",
									eventId: id
								}
							});
							break;
						default:
							never(kind);
					}
					break;
				default:
					never(status);
			}
		},
		[
			openDetailsDialog,
			openEditAnnouncementDialog,
			openEditEventDialog,
			openEditKnowledgeItemDialog
		]
	);

	const element: ReactElement | null = [
		detailsDialog,
		editAnnouncementDialog,
		editKnowledgeItemDialog,
		editEventDialog
	]
		.find(isNotNil)
			?? null;

	return [
		element,
		open
	];
}